import type { PopoverActions } from "@mui/material";
import * as React from "react";

/**
 * The shortest the popover is held to, in px: a string field's "is any of"
 * form with its option list cut to the three rows (and truncation hint) it
 * keeps (see `OptionListbox`). With less room than this below its anchor, the
 * popover takes this height anyway and moves up to fit, as MUI places it.
 */
const MIN_AVAILABLE_HEIGHT = 432;

/**
 * The gap MUI keeps between a popover and the viewport's edge
 * (`marginThreshold`), in px.
 */
const VIEWPORT_MARGIN = 16;

/** What {@link usePopoverRefit} hands an MUI `Popover`. */
export interface PopoverRefit {
  /** The popover's `action` prop. */
  action: React.RefObject<PopoverActions>;
  /** The popover's paper ref, `slotProps.paper.ref`. */
  paperRef: (paper: HTMLDivElement | null) => void;
}

/**
 * Keep an MUI `Popover` growing downward from its anchor and fitted to the
 * viewport while its content changes size.
 *
 * The paper is given `--popover-available-height`: the room between the
 * anchor and the bottom of the viewport, less the paper's own top margin, and
 * never under {@link MIN_AVAILABLE_HEIGHT}. A paper that caps its height at it
 * hangs below its anchor however tall its content, which shrinks to fit (a
 * filter form's option list) rather than scrolling as a whole.
 *
 * MUI places a popover when it opens and again only when the window resizes,
 * so a body that grows afterwards keeps the place it was given while it was
 * short: a form switching to its "is any of" checklist, or a checklist whose
 * options arrive after the popover opened. Every change to the paper's size
 * places it again here, which moves it up to fit when it is held taller than
 * the room below its anchor.
 */
export function usePopoverRefit(anchorEl: HTMLElement | null): PopoverRefit {
  const action = React.useRef<PopoverActions>(null);
  const [paper, setPaper] = React.useState<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (paper === null) {
      return;
    }
    const observer = new ResizeObserver(() => {
      action.current?.updatePosition();
    });
    observer.observe(paper);
    return () => observer.disconnect();
  }, [paper]);

  React.useEffect(() => {
    if (paper === null || anchorEl === null) {
      return;
    }
    const fit = () => {
      const below =
        window.innerHeight -
        anchorEl.getBoundingClientRect().bottom -
        parseFloat(getComputedStyle(paper).marginTop) -
        VIEWPORT_MARGIN;
      paper.style.setProperty(
        "--popover-available-height",
        `${Math.max(below, MIN_AVAILABLE_HEIGHT)}px`,
      );
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [paper, anchorEl]);

  return { action, paperRef: setPaper };
}
